import React, { useEffect, useState } from 'react';
import { userService } from '../../services/user';
import { Certificate } from '../../types';
import {
  AcademicCapIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  CheckBadgeIcon,
  SparklesIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

const Certificates: React.FC = () => {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingCert, setDownloadingCert] = useState<string | null>(null);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await api.get('/certificates/my-certificates');
      setCertificates(response.data.certificates);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadCertificate = async (certificate: Certificate) => {
    try {
      setDownloadingCert(certificate.category);
      
      // Get the PDF from the API
      const response = await api.get(`/certificates/generate/${encodeURIComponent(certificate.category)}`, {
        responseType: 'blob',
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate-${certificate.category.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('ບໍ່ສາມາດດາວໂຫຼດໃບຢັ້ງຢືນໄດ້. ກະລຸນາລອງໃໝ່ອີກຄັ້ງ.');
    } finally {
      setDownloadingCert(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-0 max-w-7xl mx-auto">
      {/* Header with gradient */}
      <div className="mb-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold">ໃບຢັ້ງຢືນຂອງຂ້ອຍ</h1>
            <p className="mt-2 text-purple-100">ສະເຫຼີມສະຫຼອງຜົນສຳເລັດຂອງທ່ານ ແລະ ດາວໂຫຼດໃບຢັ້ງຢືນທີ່ທ່ານໄດ້ຮັບ</p>
          </div>
          <AcademicCapIcon className="h-16 w-16 text-yellow-300 opacity-50" />
        </div>
      </div>

      {certificates.length === 0 ? (
        <div className="bg-white shadow-lg rounded-2xl p-16 text-center">
          <div className="max-w-md mx-auto">
            <div className="bg-indigo-100 rounded-full p-6 w-24 h-24 mx-auto mb-6">
              <AcademicCapIcon className="h-12 w-12 text-indigo-600 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">ຍັງບໍ່ມີໃບຢັ້ງຢືນ</h3>
            <p className="text-gray-600 mb-6">
              ສຳເລັດວິດີໂອທັງໝົດໃນໝວດໝູ່ເພື່ອຮັບໃບຢັ້ງຢືນການສຳເລັດ.
            </p>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center text-sm text-gray-700">
                <SparklesIcon className="h-5 w-5 text-indigo-500 mr-2 flex-shrink-0" />
                <p>ສືບຕໍ່ຊົມແລະຮຽນຮູ້ເພື່ອປົດລັອກໃບຢັ້ງຢືນທຳອິດຂອງທ່ານ!</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {certificates.map((certificate) => (
              <div
                key={certificate.certificateId}
                className="bg-white shadow-lg rounded-2xl overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105"
              >
                <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-8 relative overflow-hidden">
                  {/* Background pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute -top-4 -left-4 w-24 h-24 bg-white rounded-full"></div>
                    <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-white rounded-full"></div>
                    <div className="absolute top-1/2 left-1/2 w-20 h-20 bg-white rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <AcademicCapIcon className="h-14 w-14 text-white" />
                      <CheckBadgeIcon className="h-10 w-10 text-yellow-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      ໃບຢັ້ງຢືນການສຳເລັດ
                    </h3>
                    <p className="text-purple-100 mt-2">ຄວາມຍິ່ງເດັ່ນໃນການຮຽນຮູ້</p>
                  </div>
                </div>
                
                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">
                    {certificate.category}
                  </h4>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center text-gray-600">
                      <div className="bg-blue-100 rounded-full p-2 mr-3">
                        <CalendarIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">ເລີ່ມຕົ້ນ</p>
                        <p className="text-xs">{new Date(certificate.startedAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <div className="bg-green-100 rounded-full p-2 mr-3">
                        <CheckBadgeIcon className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">ສຳເລັດແລ້ວ</p>
                        <p className="text-xs">{new Date(certificate.completedAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <div className="bg-purple-100 rounded-full p-2 mr-3">
                        <AcademicCapIcon className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{certificate.totalVideos} ວິດີໂອ</p>
                        <p className="text-xs">ສຳເລັດຢ່າງສຳເລັດ</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-4 font-mono">
                      ID: {certificate.certificateId}
                    </p>
                    <button
                      onClick={() => downloadCertificate(certificate)}
                      disabled={downloadingCert === certificate.category}
                      className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {downloadingCert === certificate.category ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          ກຳລັງສ້າງ PDF...
                        </>
                      ) : (
                        <>
                          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                          ດາວໂຫຼດໃບຢັ້ງຢືນ
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Achievement Stats */}
          <div className="mt-12 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="bg-white rounded-full p-3 shadow-sm">
                  <SparklesIcon className="h-8 w-8 text-indigo-600" />
                </div>
              </div>
              <div className="ml-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  ຜົນສຳເລັດທີ່ຍິ່ງໃຫຍ່!
                </h3>
                <p className="text-gray-700 mb-4">
                  ທ່ານໄດ້ຮັບໃບຢັ້ງຢືນ {certificates.length} ໃບ! 
                  ຄວາມອຸທິດຕົນຂອງທ່ານໃນການຮຽນຮູ້ເປັນແຮງບັນດານໃຈຢ່າງແທ້ຈິງ.
                </p>
                <div className="flex flex-wrap gap-3">
                  {certificates.map((cert) => (
                    <span
                      key={cert.certificateId}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white text-indigo-700 border border-indigo-200"
                    >
                      <CheckBadgeIcon className="h-4 w-4 mr-1" />
                      {cert.category}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Certificates;